import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Heart, MessageCircle, TreePine, Eye, Clock, TrendingUp } from 'lucide-react';
import { collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import { db } from '../lib/firebase';
import toast from 'react-hot-toast';
import ImageWithFallback from './ImageWithFallback';
import { createTreeSlug } from '../utils/slug';
import SkeletonLoader from './SkeletonLoader';

interface Tree {
  id: string;
  name: string;
  creator: string;
  imageUrl: string;
  votes: number;
  commentCount?: number;
  views?: number;
  createdAt: Date;
}

interface StatsData {
  totalTrees: number;
  totalVotes: number;
  totalComments: number;
  totalViews: number;
}

type SortOption = 'recent' | 'votes' | 'comments' | 'views';

const TreeCard = React.memo(({ tree }: { tree: Tree }) => (
  <div className="group relative bg-white rounded-xl shadow-lg overflow-hidden transform hover:-translate-y-1 transition-all duration-300">
    <Link 
      to={`/arbol/${createTreeSlug(tree.name, tree.id)}`}
      className="block"
    >
      <div className="aspect-[4/3] overflow-hidden relative">
        <ImageWithFallback 
          src={tree.imageUrl} 
          alt={tree.name}
          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
          <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
            <h3 className="font-bold text-lg mb-1">{tree.name}</h3>
            <p className="text-sm opacity-90">por {tree.creator}</p>
          </div>
        </div>
      </div>
      
      <div className="p-4">
        <div className="flex items-center justify-between text-sm">
          <div className="flex items-center gap-4">
            <div className="flex items-center text-rose-500">
              <Heart className="h-4 w-4 mr-1" />
              <span className="font-medium">{tree.votes || 0}</span>
            </div>
            {tree.commentCount !== undefined && (
              <div className="flex items-center text-indigo-500">
                <MessageCircle className="h-4 w-4 mr-1" />
                <span className="font-medium">{tree.commentCount}</span>
              </div>
            )}
            {tree.views !== undefined && (
              <div className="flex items-center text-emerald-500">
                <Eye className="h-4 w-4 mr-1" />
                <span className="font-medium">{tree.views}</span>
              </div>
            )}
          </div>
          <div className="text-gray-500 text-xs">
            {tree.createdAt?.toLocaleDateString()}
          </div>
        </div>
      </div>
    </Link>
  </div>
));

export default function Gallery() {
  const [trees, setTrees] = useState<Tree[]>([]);
  const [stats, setStats] = useState<StatsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState<SortOption>('recent');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const treesQuery = query(
          collection(db, 'trees'),
          where('status', '==', 'approved'),
          orderBy('createdAt', 'desc')
        );
        const snapshot = await getDocs(treesQuery);
        
        let totalVotes = 0;
        let totalViews = 0;
        
        const treeData = await Promise.all(snapshot.docs.map(async (doc) => {
          const data = doc.data();
          totalVotes += data.votes || 0;
          totalViews += data.views || 0;
          
          const commentsQuery = query(
            collection(db, 'comments'),
            where('treeId', '==', doc.id)
          );
          const commentsSnapshot = await getDocs(commentsQuery);
          
          return {
            id: doc.id,
            ...data,
            createdAt: data.createdAt?.toDate(),
            commentCount: commentsSnapshot.size
          };
        }));

        const commentsQuery = query(collection(db, 'comments'));
        const commentsSnapshot = await getDocs(commentsQuery);

        setTrees(treeData);
        setStats({
          totalTrees: snapshot.size,
          totalVotes,
          totalComments: commentsSnapshot.size,
          totalViews
        });
      } catch (error) {
        console.error('Error fetching data:', error);
        toast.error('Error al cargar los datos');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const sortedTrees = [...trees].sort((a, b) => {
    switch (sortBy) {
      case 'votes':
        return (b.votes || 0) - (a.votes || 0);
      case 'comments':
        return (b.commentCount || 0) - (a.commentCount || 0);
      case 'views':
        return (b.views || 0) - (a.views || 0);
      default:
        return b.createdAt.getTime() - a.createdAt.getTime();
    }
  });

  const SortButton = ({ option, icon: Icon, label }: { option: SortOption; icon: any; label: string }) => (
    <button
      onClick={() => setSortBy(option)}
      className={`flex items-center px-4 py-2 rounded-full text-sm font-medium transition-colors ${
        sortBy === option
          ? 'bg-primary text-white'
          : 'bg-white text-gray-600 hover:bg-gray-50'
      }`}
    >
      <Icon className="h-4 w-4 mr-2" />
      {label}
    </button>
  );

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-12">
        {/* Stats Cards Skeleton */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-12">
          {Array.from({ length: 4 }).map((_, index) => (
            <div key={index} className="bg-white p-6 rounded-xl shadow-lg">
              <div className="flex justify-between">
                <div className="space-y-3">
                  <SkeletonLoader variant="text" className="w-24" />
                  <SkeletonLoader variant="text" className="w-16 h-8" />
                  <SkeletonLoader variant="text" className="w-32" />
                </div>
                <SkeletonLoader variant="circular" width={32} height={32} />
              </div>
            </div>
          ))}
        </div>

        {/* Sort Buttons Skeleton */}
        <div className="flex flex-wrap gap-2 mb-8">
          {Array.from({ length: 4 }).map((_, index) => (
            <SkeletonLoader key={index} className="w-32 h-10 rounded-full" />
          ))}
        </div>

        {/* Trees Grid Skeleton */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {Array.from({ length: 8 }).map((_, index) => (
            <div key={index} className="bg-white rounded-xl shadow-lg overflow-hidden">
              <SkeletonLoader className="w-full aspect-[4/3]" />
              <div className="p-4 space-y-3">
                <SkeletonLoader variant="text" className="w-3/4" />
                <SkeletonLoader variant="text" className="w-1/2" />
                <div className="flex justify-between items-center pt-2">
                  <div className="flex gap-4">
                    <SkeletonLoader variant="text" className="w-12" />
                    <SkeletonLoader variant="text" className="w-12" />
                    <SkeletonLoader variant="text" className="w-12" />
                  </div>
                  <SkeletonLoader variant="text" className="w-20" />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-12">
      <h2 className="text-3xl font-bold text-center mb-12 text-primary">
        Galería de Participantes
      </h2>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-12">
        <div className="bg-gradient-to-br from-primary/90 to-primary-light p-6 rounded-xl text-white shadow-lg hover:shadow-xl transition-shadow">
          <TreePine className="h-8 w-8 mb-2 opacity-80" />
          <p className="text-sm opacity-80">Participantes</p>
          <h3 className="text-2xl font-bold">{stats?.totalTrees || 0}</h3>
        </div>

        <div className="bg-gradient-to-br from-rose-400 to-rose-500 p-6 rounded-xl text-white shadow-lg hover:shadow-xl transition-shadow">
          <Heart className="h-8 w-8 mb-2 opacity-80" />
          <p className="text-sm opacity-80">Votos</p>
          <h3 className="text-2xl font-bold">{stats?.totalVotes || 0}</h3>
        </div>

        <div className="bg-gradient-to-br from-indigo-400 to-indigo-500 p-6 rounded-xl text-white shadow-lg hover:shadow-xl transition-shadow">
          <MessageCircle className="h-8 w-8 mb-2 opacity-80" />
          <p className="text-sm opacity-80">Comentarios</p>
          <h3 className="text-2xl font-bold">{stats?.totalComments || 0}</h3>
        </div>

        <div className="bg-gradient-to-br from-emerald-400 to-emerald-500 p-6 rounded-xl text-white shadow-lg hover:shadow-xl transition-shadow">
          <Eye className="h-8 w-8 mb-2 opacity-80" />
          <p className="text-sm opacity-80">Visualizaciones</p>
          <h3 className="text-2xl font-bold">{stats?.totalViews || 0}</h3>
        </div>
      </div>

      {/* Sort Filters */}
      <div className="flex flex-wrap gap-2 mb-8">
        <SortButton option="recent" icon={Clock} label="Más recientes" />
        <SortButton option="votes" icon={TrendingUp} label="Más votados" />
        <SortButton option="comments" icon={MessageCircle} label="Más comentados" />
        <SortButton option="views" icon={Eye} label="Más vistos" />
      </div>

      {/* Trees Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {sortedTrees.map((tree) => (
          <TreeCard key={tree.id} tree={tree} />
        ))}
      </div>
      
      {trees.length === 0 && (
        <div className="text-center py-12 bg-white rounded-xl shadow-lg">
          <TreePine className="h-16 w-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-600">No hay árboles para mostrar todavía.</p>
          <p className="text-gray-500 mt-2">¡Sé el primero en participar!</p>
        </div>
      )}
    </div>
  );
}